/**
 * Core scroll utility function that works in any context
 *
 * @param {HTMLElement} content - The scrollable content container
 * @param {number}      index   - The index to scroll to
 */
export function scrollToIndex( content, index ) {
	if ( ! content || ! content.children || ! content.children[ index ] ) {
		return;
	}

	const child = content.children[ index ];

	let scrollLeft;

	if ( child.offsetLeft !== undefined && child.offsetLeft > 0 ) {
		scrollLeft = child.offsetLeft;
	} else {
		const containerWidth = content.offsetWidth || content.clientWidth;
		scrollLeft = index * containerWidth;
	}

	content.scrollTo( {
		left: scrollLeft,
		top: 0,
		behavior: 'smooth',
	} );
}

/**
 * Get the current scroll index based on scroll position
 *
 * @param {HTMLElement} content - The scrollable content container
 * @return {number} Current index
 */
export function getCurrentScrollIndex( content ) {
	if ( ! content || ! content.offsetWidth ) {
		return 0;
	}
	return Math.round( content.scrollLeft / content.offsetWidth );
}

export class Testimonials {
	constructor( node, opts = {} ) {
		if ( ! node ) {
			return;
		}

		this.node = node;
		this.opts = {
			content: '.testimonials__content',
			next: '.testimonials__next',
			prev: '.testimonials__prev',
			pagination: '.testimonials__pagination',
			autoplay: false,
			interval: 5000,
			...opts,
		};

		this.content = node.querySelector( this.opts.content );
		this.prev = node.querySelector( this.opts.prev );
		this.next = node.querySelector( this.opts.next );
		this.pagination = node.querySelector( this.opts.pagination );

		if ( this.opts.autoplay ) {
			this.interval = setInterval(
				() => {
					if ( getCurrentScrollIndex( this.content ) >= this.content.children.length - 1 ) {
						return scrollToIndex( this.content, 0 );
					}

					return this.scrollNext();
				},
				parseInt( this.opts.interval, 10 )
			);
		}

		if ( this.prev ) {
			this.prev.addEventListener( 'click', () => {
				this.scrollPrev();

				if ( this.interval ) {
					clearInterval( this.interval );
				}
			} );
		}

		if ( this.next ) {
			this.next.addEventListener( 'click', () => {
				this.scrollNext();

				if ( this.interval ) {
					clearInterval( this.interval );
				}
			} );
		}

		this.content.addEventListener( 'scroll', this.setState.bind( this ) );
		scrollToIndex( this.content, 0 );
		this.setState();
	}

	scrollPrev() {
		const currentIndex = getCurrentScrollIndex( this.content );
		scrollToIndex( this.content, Math.max( currentIndex - 1, 0 ) );
	}

	scrollNext() {
		const currentIndex = getCurrentScrollIndex( this.content );
		scrollToIndex( this.content, Math.min( currentIndex + 1, this.content.children.length - 1 ) );
	}

	setState() {
		const index = getCurrentScrollIndex( this.content );

		if ( index >= this.content.children.length - 1 ) {
			if ( this.next ) {
				this.next.disabled = true;
			}

			if ( this.prev ) {
				this.prev.removeAttribute( 'disabled' );
			}
		} else if ( index <= 0 ) {
			if ( this.next ) {
				this.next.removeAttribute( 'disabled' );
			}

			if ( this.prev ) {
				this.prev.disabled = true;
			}
		} else {
			if ( this.next ) {
				this.next.removeAttribute( 'disabled' );
			}

			if ( this.prev ) {
				this.prev.removeAttribute( 'disabled' );
			}
		}

		if ( this.pagination ) {
			[ ...this.pagination.children ].forEach( ( dot, i ) => {
				dot.classList.toggle( 'is-active', i === index );
			} );
		}
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.testimonials' ).forEach( ( node ) => {
		new Testimonials( node, { ...node.dataset } );
	} );
} );
